/* ==========================================================================
   Kalter Bruder® – Podologie-Landingpage
   Gekapselt: greift ausschließlich innerhalb von #kb-podologie-landingpage zu.
   Kein Framework, keine externen Requests.
   ========================================================================== */
(() => {
  "use strict";

  const root = document.querySelector("#kb-podologie-landingpage");
  if (!root) return;

  root.classList.add("kb-js");

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const supportsIO = "IntersectionObserver" in window;

  /* ---------- 1. Sticky Header: kompakter Zustand ------------------------ */
  const header = root.querySelector("[data-header]");
  if (header) {
    let ticking = false;
    const updateHeader = () => {
      header.classList.toggle("is-compact", window.scrollY > 24);
      ticking = false;
    };
    updateHeader();
    window.addEventListener(
      "scroll",
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(updateHeader);
      },
      { passive: true }
    );
  }

  /* ---------- 2. Reveal beim Scrollen ----------------------------------- */
  const revealItems = root.querySelectorAll(".kb-reveal");
  if (supportsIO) {
    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
    );
    revealItems.forEach((item) => revealObserver.observe(item));
  } else {
    revealItems.forEach((item) => item.classList.add("is-visible"));
  }

  /* ---------- 3. Sanftes Scrollen für Sprungmarken ---------------------- */
  root.querySelectorAll("[data-kb-anchor], .kb-wordmark").forEach((link) => {
    link.addEventListener("click", (event) => {
      const href = link.getAttribute("href") || "";
      if (href.charAt(0) !== "#" || href.length < 2) return;
      const target = root.querySelector(href);
      if (!target) return;
      event.preventDefault();

      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      const targetTop = target.getBoundingClientRect().top + window.scrollY;
      const destination = Math.max(0, targetTop - headerHeight - 20);

      window.scrollTo({
        top: destination,
        behavior: reduceMotion.matches ? "auto" : "smooth",
      });
    });
  });

  /* ---------- 4. Fachhandels-Marquee (CSS-Animation, IO-gesteuert) ------ */
  const marquee = root.querySelector("[data-logo-marquee]");
  const logoSequence = marquee && marquee.querySelector("[data-logo-sequence]");
  if (marquee && logoSequence) {
    const logoTrack = marquee.querySelector(".kb-logo-track");

    /* Der Klon verweist auf identische Bildquellen – keine zusätzlichen Bytes. */
    const clone = logoSequence.cloneNode(true);
    clone.removeAttribute("data-logo-sequence");
    clone.setAttribute("data-logo-clone", "");
    clone.setAttribute("aria-hidden", "true");
    clone.querySelectorAll("img").forEach((image) => {
      image.alt = "";
    });
    logoSequence.after(clone);

    /* Wichtig: Der Lauf startet bei der ersten Podologen-Karte und bleibt bis
       zum ersten tatsächlichen Sichtkontakt vollständig stehen. */
    marquee.classList.add("is-ready", "is-paused");

    const logoItems = Array.from(marquee.querySelectorAll(".kb-logo-row li"));
    let marqueeInView = false;
    let marqueeStarted = false;
    let lastLogoBendUpdate = 0;

    const syncMarqueePlayback = () => {
      const shouldPlay = marqueeInView && !document.hidden && !reduceMotion.matches;
      marquee.classList.toggle("is-paused", !shouldPlay);
    };

    const startMarqueeAtBeginning = () => {
      if (!logoTrack || marqueeStarted) return;
      marqueeStarted = true;

      /* Animation garantiert bei 0 % neu anlegen, bevor sie freigegeben wird. */
      logoTrack.style.animation = "none";
      void logoTrack.offsetWidth;
      logoTrack.style.removeProperty("animation");
    };

    const resetLogoBend = () => {
      logoItems.forEach((item) => {
        item.style.removeProperty("--kb-logo-bend");
        item.style.removeProperty("--kb-logo-depth");
        item.style.removeProperty("--kb-logo-lift");
        item.style.removeProperty("--kb-logo-scale");
        item.style.removeProperty("--kb-logo-opacity");
      });
    };

    const updateLogoBend = (timestamp = 0) => {
      if (reduceMotion.matches) {
        resetLogoBend();
        return;
      }

      if (marqueeInView && !document.hidden && timestamp - lastLogoBendUpdate >= 32) {
        lastLogoBendUpdate = timestamp;
        const marqueeBounds = marquee.getBoundingClientRect();
        if (marqueeBounds.width > 0) {
          const marqueeCenter = marqueeBounds.left + marqueeBounds.width / 2;
          const halfWidth = marqueeBounds.width / 2;
          const strength = window.innerWidth <= 800 ? 0.62 : 1;

          logoItems.forEach((item) => {
            const itemBounds = item.getBoundingClientRect();
            const itemCenter = itemBounds.left + itemBounds.width / 2;
            const offset = Math.max(-1, Math.min(1, (itemCenter - marqueeCenter) / halfWidth));
            const distance = Math.abs(offset);

            item.style.setProperty("--kb-logo-bend", `${(-offset * 10 * strength).toFixed(2)}deg`);
            item.style.setProperty("--kb-logo-depth", `${(-distance * 22 * strength).toFixed(2)}px`);
            item.style.setProperty("--kb-logo-lift", `${(distance * 4 * strength).toFixed(2)}px`);
            item.style.setProperty("--kb-logo-scale", (1.015 - distance * 0.055 * strength).toFixed(3));
            item.style.setProperty("--kb-logo-opacity", (1 - distance * 0.16 * strength).toFixed(3));
          });
        }
      }

      window.requestAnimationFrame(updateLogoBend);
    };

    window.requestAnimationFrame(updateLogoBend);
    if (reduceMotion.addEventListener) {
      reduceMotion.addEventListener("change", (event) => {
        resetLogoBend();
        syncMarqueePlayback();
        if (!event.matches) window.requestAnimationFrame(updateLogoBend);
      });
    }

    document.addEventListener("visibilitychange", syncMarqueePlayback);

    if (supportsIO) {
      const marqueeObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            marqueeInView = entry.isIntersecting && entry.intersectionRatio >= 0.12;
            if (marqueeInView) startMarqueeAtBeginning();
            syncMarqueePlayback();
          });
        },
        { threshold: [0, 0.12] }
      );
      marqueeObserver.observe(marquee);
    } else {
      marqueeInView = true;
      startMarqueeAtBeginning();
      syncMarqueePlayback();
    }
  }

  /* ---------- 5. Einsatzbereiche: räumlicher Slider --------------------- */
  const useCarousel = root.querySelector("[data-use-carousel]");
  if (useCarousel) {
    const useTrack = useCarousel.querySelector("[data-use-track]");
    const useSlides = Array.from(useCarousel.querySelectorAll(".kb-use-case"));
    const usePrevious = useCarousel.querySelector("[data-use-previous]");
    const useNext = useCarousel.querySelector("[data-use-next]");
    const usePosition = useCarousel.querySelector("[data-use-position]");
    const useDots = Array.from(useCarousel.querySelectorAll("[data-use-dot]"));
    let activeUseSlide = 0;
    let useTouchStart = null;
    let useTransitioning = false;
    let queuedUseSlide = null;
    let useTransitionTimer = 0;

    useSlides.forEach((slide, index) => {
      slide.setAttribute("role", "group");
      slide.setAttribute("aria-roledescription", "Folie");
      slide.setAttribute("aria-label", `${index + 1} von ${useSlides.length}`);
    });

    const normalizeUseIndex = (index) =>
      (index + useSlides.length) % useSlides.length;

    const setUseSlideState = (requestedIndex) => {
      if (!useSlides.length) return;
      activeUseSlide = normalizeUseIndex(requestedIndex);
      const previousIndex = (activeUseSlide - 1 + useSlides.length) % useSlides.length;
      const nextIndex = (activeUseSlide + 1) % useSlides.length;
      const farPreviousIndex = (activeUseSlide - 2 + useSlides.length) % useSlides.length;
      const farNextIndex = (activeUseSlide + 2) % useSlides.length;

      useSlides.forEach((slide, index) => {
        const active = index === activeUseSlide;
        slide.classList.toggle("is-active", active);
        slide.classList.toggle("is-previous", index === previousIndex);
        slide.classList.toggle("is-next", index === nextIndex);
        slide.classList.toggle("is-far-previous", index === farPreviousIndex);
        slide.classList.toggle("is-far-next", index === farNextIndex);
        slide.setAttribute("aria-hidden", String(!active));
      });

      useDots.forEach((dot, index) => {
        if (index === activeUseSlide) dot.setAttribute("aria-current", "true");
        else dot.removeAttribute("aria-current");
      });

      if (usePosition) {
        usePosition.textContent = `${activeUseSlide + 1} / ${useSlides.length}`;
      }
    };

    const finishUseTransition = () => {
      window.clearTimeout(useTransitionTimer);
      useTransitioning = false;
          useCarousel.classList.remove("is-moving", "is-switching", "is-repositioning");
      useCarousel.removeAttribute("aria-busy");

      if (queuedUseSlide === null || queuedUseSlide === activeUseSlide) {
        queuedUseSlide = null;
        return;
      }

      const nextQueuedSlide = queuedUseSlide;
      queuedUseSlide = null;
      window.requestAnimationFrame(() => showUseSlide(nextQueuedSlide));
    };

    const showUseSlide = (requestedIndex, immediate = false) => {
      if (!useSlides.length) return;
      const normalizedIndex = normalizeUseIndex(requestedIndex);

      if (!immediate && useTransitioning) {
        queuedUseSlide = normalizedIndex;
        return;
      }

      if (!immediate && normalizedIndex === activeUseSlide) return;

      if (immediate || reduceMotion.matches) {
        setUseSlideState(normalizedIndex);
        return;
      }

          useTransitioning = true;
          useCarousel.classList.add("is-moving", "is-switching");
          useCarousel.setAttribute("aria-busy", "true");

          window.clearTimeout(useTransitionTimer);
          useTransitionTimer = window.setTimeout(() => {
            useCarousel.classList.add("is-repositioning");
            setUseSlideState(normalizedIndex);

            /* Die Karten werden im ausgeblendeten Zustand ohne Bewegung
               auf ihre neuen festen Positionen gesetzt. */
            void useCarousel.offsetWidth;

            useCarousel.classList.remove("is-repositioning");
            window.requestAnimationFrame(() => {
              useCarousel.classList.remove("is-switching");
              useTransitionTimer = window.setTimeout(finishUseTransition, 480);
            });
          }, 155);
    };

    if (usePrevious) {
      usePrevious.addEventListener("click", () => showUseSlide(activeUseSlide - 1));
    }

    if (useNext) {
      useNext.addEventListener("click", () => showUseSlide(activeUseSlide + 1));
    }

    useDots.forEach((dot, index) => {
      dot.addEventListener("click", () => showUseSlide(index));
    });

    if (useTrack) {
      useTrack.addEventListener("keydown", (event) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          showUseSlide(activeUseSlide - 1);
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          showUseSlide(activeUseSlide + 1);
        } else if (event.key === "Home") {
          event.preventDefault();
          showUseSlide(0);
        } else if (event.key === "End") {
          event.preventDefault();
          showUseSlide(useSlides.length - 1);
        }
      });

      useTrack.addEventListener(
        "touchstart",
        (event) => {
          const touch = event.changedTouches[0];
          useTouchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
        },
        { passive: true }
      );

      useTrack.addEventListener(
        "touchend",
        (event) => {
          if (!useTouchStart) return;
          const touch = event.changedTouches[0];
          if (!touch) return;
          const deltaX = touch.clientX - useTouchStart.x;
          const deltaY = touch.clientY - useTouchStart.y;
          useTouchStart = null;
          if (Math.abs(deltaX) < 42 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
          showUseSlide(activeUseSlide + (deltaX < 0 ? 1 : -1));
        },
        { passive: true }
      );
    }

    useCarousel.classList.add("is-initializing");
    showUseSlide(0, true);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => useCarousel.classList.remove("is-initializing"));
    });
  }

  /* ---------- 6. Inhaltsstoff-Karten: umdrehen (Touch + Tastatur) ------- */
  root.querySelectorAll("[data-flip-card]").forEach((card) => {
    card.addEventListener("click", () => {
      const open = card.getAttribute("aria-expanded") === "true";
      card.setAttribute("aria-expanded", String(!open));
    });
  });

  /* ---------- 6. POS-Aufsteller: Vorder-/Rückseite ---------------------- */
  const posFlip = root.querySelector("[data-pos-flip]");
  if (posFlip) {
    const posFront = posFlip.querySelector("[data-pos-front]");
    const posBack = posFlip.querySelector("[data-pos-back]");
    let showsBack = false;
    let timer = null;
    let paused = false;

    const showFace = (back) => {
      showsBack = back;
      posFlip.classList.toggle("is-flipped", back);
      posFlip.setAttribute("aria-pressed", String(back));
      posFlip.setAttribute(
        "aria-label",
        back
          ? "POS-Aufsteller, Rückseite. Aktivieren, um die Vorderseite zu zeigen."
          : "POS-Aufsteller, Vorderseite. Aktivieren, um die Rückseite zu zeigen."
      );
      if (posFront) posFront.setAttribute("aria-hidden", String(back));
      if (posBack) posBack.setAttribute("aria-hidden", String(!back));
    };

    const stop = () => {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    };

    const start = () => {
      stop();
      if (reduceMotion.matches || document.hidden || paused) return;
      timer = window.setInterval(() => showFace(!showsBack), 5000);
    };

    showFace(false);
    start();

    posFlip.addEventListener("click", () => {
      showFace(!showsBack);
      start();
    });
    ["mouseenter", "focus"].forEach((type) =>
      posFlip.addEventListener(type, () => {
        paused = true;
        stop();
      })
    );
    ["mouseleave", "blur"].forEach((type) =>
      posFlip.addEventListener(type, () => {
        paused = false;
        start();
      })
    );
    document.addEventListener("visibilitychange", start);
    if (reduceMotion.addEventListener) reduceMotion.addEventListener("change", start);

    /* Auto-Flip anhalten, solange der Aufsteller nicht im Blickfeld ist. */
    if (supportsIO) {
      const posObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) start();
            else stop();
          });
        },
        { threshold: 0.15 }
      );
      posObserver.observe(posFlip);
    }
  }

  /* ---------- 7. Erfahrungsberichte: Slider + „Weiterlesen“ ------------- */
  const track = root.querySelector("[data-review-track]");
  if (track) {
    const cards = Array.from(track.querySelectorAll(".kb-review-card"));
    const prev = root.querySelector("[data-review-prev]");
    const next = root.querySelector("[data-review-next]");
    const position = root.querySelector("[data-review-position]");

    /* „Weiterlesen“ nur dort einsetzen, wo der Text tatsächlich abgeschnitten ist. */
    const addReadMore = () => {
      cards.forEach((card) => {
        if (card.querySelector(".kb-review-card__more")) return;
        const text = card.querySelector("blockquote p");
        const author = card.querySelector(".kb-review-card__author");
        if (!text || !author) return;
        if (text.scrollHeight <= text.clientHeight + 4) return;

        const button = document.createElement("button");
        button.type = "button";
        button.className = "kb-review-card__more";
        button.textContent = "Ganzen Bericht lesen";
        button.setAttribute("aria-expanded", "false");
        button.addEventListener("click", () => {
          const open = card.classList.toggle("is-expanded");
          button.setAttribute("aria-expanded", String(open));
          button.textContent = open ? "Bericht einklappen" : "Ganzen Bericht lesen";
        });
        author.before(button);
      });
    };

    const step = () => {
      if (cards.length < 2) return track.clientWidth;
      return cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left;
    };

    const perView = () => {
      const width = step();
      if (!width) return 1;
      return Math.max(1, Math.round(track.clientWidth / width));
    };

    const updateState = () => {
      const width = step();
      const visible = perView();
      const first = width ? Math.round(track.scrollLeft / width) : 0;
      const from = Math.min(first + 1, cards.length);
      const to = Math.min(first + visible, cards.length);

      if (position) {
        position.textContent =
          visible > 1 ? from + " – " + to + " von " + cards.length : from + " von " + cards.length;
      }
      const atStart = track.scrollLeft <= 4;
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;
    };

    const page = (direction) => {
      track.scrollBy({
        left: direction * step() * perView(),
        behavior: reduceMotion.matches ? "auto" : "smooth",
      });
    };

    if (prev) prev.addEventListener("click", () => page(-1));
    if (next) next.addEventListener("click", () => page(1));

    track.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        page(-1);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        page(1);
      }
    });

    let scrollTick = false;
    track.addEventListener(
      "scroll",
      () => {
        if (scrollTick) return;
        scrollTick = true;
        window.requestAnimationFrame(() => {
          updateState();
          scrollTick = false;
        });
      },
      { passive: true }
    );

    const refresh = () => {
      addReadMore();
      updateState();
    };

    refresh();
    window.addEventListener("resize", refresh, { passive: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);
  }

  /* ---------- 8. Mobile Sticky-CTA -------------------------------------- */
  const stickyCta = root.querySelector("[data-sticky-cta]");
  const hero = root.querySelector(".kb-hero");
  const contact = root.querySelector("#kontakt");
  if (stickyCta && hero) {
    const stickyLink = stickyCta.querySelector("a");
    let heroPassed = false;
    let contactVisible = false;

    const apply = () => {
      const show = heroPassed && !contactVisible;
      stickyCta.classList.toggle("is-visible", show);
      stickyCta.setAttribute("aria-hidden", String(!show));
      if (stickyLink) stickyLink.tabIndex = show ? 0 : -1;
    };

    if (supportsIO) {
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            heroPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            apply();
          });
        },
        { threshold: 0 }
      ).observe(hero);

      if (contact) {
        new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              contactVisible = entry.isIntersecting;
              apply();
            });
          },
          { threshold: 0.06 }
        ).observe(contact);
      }
    }
  }

  /* ---------- 9. Jotform: Höhe per postMessage nachführen --------------- */
  const jotform = root.querySelector("[data-jotform]");
  if (jotform) {
    window.addEventListener("message", (event) => {
      if (typeof event.origin !== "string" || event.origin.indexOf("jotform.com") === -1) return;
      const data = event.data;
      if (typeof data !== "string" || data.indexOf("setHeight") !== 0) return;
      const height = parseInt(data.split(":")[1], 10);
      if (!height || height < 200 || height > 20000) return;
      jotform.style.height = height + "px";
      jotform.style.minHeight = "0px";
    });
  }
})();
